// src/components/files/FolderDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
} from '@mui/material';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { useFileManager } from '../../contexts/FileManagerContext';

interface FolderDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'rename';
  folderId?: number;
  initialName?: string;
}

const FolderDialog: React.FC<FolderDialogProps> = ({
  open,
  onClose,
  mode,
  folderId,
  initialName = '',
}) => {
  const { t } = useTranslation();
  const { createFolder, renameFolder } = useFileManager();
  const [name, setName] = useState(initialName);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setError(null);
    }
  }, [open, initialName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError(t('fileManager.folder.nameRequired', 'Le nom est requis'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (mode === 'create') {
        await createFolder(name.trim());
      } else if (mode === 'rename' && folderId) {
        await renameFolder(folderId, name.trim());
      }
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('fileManager.folder.error', 'Une erreur est survenue'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {mode === 'create' ? (
            <>
              <CreateNewFolderIcon color="primary" />
              {t('fileManager.folder.createTitle', 'Nouveau dossier')}
            </>
          ) : (
            <>
              <EditIcon color="primary" />
              {t('fileManager.folder.renameTitle', 'Renommer le dossier')}
            </>
          )}
        </DialogTitle>

        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t('fileManager.folder.nameLabel', 'Nom du dossier')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!error}
            helperText={error}
            disabled={loading}
            sx={{ mt: 1 }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !name.trim()}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {mode === 'create'
              ? t('fileManager.folder.create', 'Creer')
              : t('fileManager.folder.rename', 'Renommer')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FolderDialog;
